import React, { Fragment, useEffect, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import { CardBody, Row, Col, Table } from 'reactstrap'
import { API_URL } from './utils/Constants/URLS'
import { useParams, useLocation } from 'react-router-dom'
import { CSVLink } from 'react-csv'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons'
import PageHeader from './utils/PageHeader'
import PageLoader from './utils/PageLoader'
import { useSelector } from 'react-redux'

/** Roles permitted to raise a manual Store PR */
const STORE_PR_ROLES = ['super_admin', 'commercial_manager', 'store_manager']

const BatchDetails = () => {
  const token = localStorage.getItem('token')
  const location = useLocation()
  const { modelid, storeid } = useParams()
  const [pageLoader, setPageLoader] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showDeclineModal, setShowDeclineModal] = useState(false)
  const [bulkUploadModal, setBulkUploadModal] = useState(false)
  const [batchDate, setBatchDate] = useState('')
  const [modelFile, setModelFile] = useState(null)
  const [modalMessage, setModalMessage] = useState('')
  const [editedBatchDate, setEditedBatchDate] = useState('')
  const [editedPartCode, setEditedPartCode] = useState('')
  const [quantity, setQuantity] = useState(0)
  const [editedQuatinty, setEditedQuantity] = useState(0)
  const [purchasePrice, setPurchasePrice] = useState(0)
  const [editedPurchasePrice, setEditedPurchasePrice] = useState(null)
  const [listPrice, setListPrice] = useState(0)
  const [editedListPrice, setEditedListPrice] = useState(null)
  const [models, setModels] = useState([])
  const [isEdit, setIsEdit] = useState(null)
  const [minQuantity, setMinQuantity] = useState(0)
  const [maxQuantity, setMaxQuantity] = useState(0)
  const [editedMinQuantity, setEditedMinQuantity] = useState(0)
  const [editedMaxQuantity, setEditedMaxQuantity] = useState(0)
  const userDetails = useSelector(store => store.user)

  /* ── Raise PR modal state ── */
  const [showRaisePRModal, setShowRaisePRModal] = useState(false)
  const [prQuantity, setPrQuantity] = useState('')
  const [prDeliveryDate, setPrDeliveryDate] = useState('')
  const [prSpecification, setPrSpecification] = useState('')
  const [prIsOem, setPrIsOem] = useState(false)
  const [prLoading, setPrLoading] = useState(false)
  const [prError, setPrError] = useState('')

  const modelname = location.state?.modelName || ''
  const partcode = location.state?.partCode || ''

  const handleCloseModal = () => {
    setShowSuccessModal(false)
    setShowDeclineModal(false)
    setIsEdit(null)
    window.location.reload()
  }

  const handleAddBatches = async e => {
    e.preventDefault()
    try {
      const url = `${API_URL}models/${modelid}/batches`
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'Application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          batchDate,
          quantity,
          minQuantity,
          maxQuantity,
          purchasePrice,
          listPrice,
          store: storeid
        })
      })
      const result = await res.json()
      if (result.error === false) {
        setModalMessage('Batches Added Successfully')
        setShowSuccessModal(true)
      } else {
        setShowDeclineModal(true)
      }
    } catch (e) {
      setShowDeclineModal(true)
    }
  }

  const handleEditBatches = async (e, id) => {
    e.preventDefault()
    const url = `${API_URL}models/batches/${id}`
    try {
      const res = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          batchDate: editedBatchDate,
          partCode: editedPartCode,
          quantity: editedQuatinty,
          minQuantity: editedMinQuantity,
          maxQuantity: editedMaxQuantity,
          purchasePrice: editedPurchasePrice,
          listPrice: editedListPrice,
        })
      })
      const result = await res.json()

      if (result.error === false) {
        setModalMessage('Batch Updated Successfully')
        setShowSuccessModal(true)
      } else {
        setShowDeclineModal(true)
      }
    } catch (e) {
      setShowDeclineModal(true)
    }
  }

  const handleAddBulkModel = async e => {
    e.preventDefault()
    setPageLoader(true)
    const formData = new FormData()
    formData.append('csv', modelFile)
    try {
      const url = `${API_URL}models/bulk`
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData,
        redirect: 'follow'
      })
      const result = await res.json()
      if (result.error === false) {
        setModalMessage('All Models in Inventory Added Successfully!')
        setBulkUploadModal(false)
        setShowSuccessModal(true)
      } else {
        setShowDeclineModal(true)
      }
    } catch (e) {
      console.log(e)
    } finally {
      setPageLoader(false)
    }
  }

  /* ── Open PR modal — pre-fill quantity with maxQuantity of the latest batch ── */
  const handleOpenRaisePR = () => {
    const latestBatch = models.length > 0 ? models[models.length - 1] : null
    setPrQuantity(latestBatch?.maxQuantity || '')
    setPrDeliveryDate('')
    setPrSpecification('')
    setPrIsOem(false)
    setPrError('')
    setShowRaisePRModal(true)
  }

  /* ── Submit the Store PR ── */
  const handleRaisePR = async e => {
    e.preventDefault()
    setPrError('')

    if (!prQuantity || Number(prQuantity) <= 0) {
      setPrError('Quantity must be greater than 0.')
      return
    }
    if (!prDeliveryDate) {
      setPrError('Required delivery date is mandatory.')
      return
    }

    setPrLoading(true)
    try {
      const res = await fetch(`${API_URL}purchase-requests/store`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          storeId: storeid,
          modelId: modelid,
          quantity: Number(prQuantity),
          requiredDeliveryDate: prDeliveryDate,
          specification: prSpecification || undefined,
          isOem: prIsOem
        })
      })
      const result = await res.json()

      if (result.error === false) {
        setShowRaisePRModal(false)
        setModalMessage(`Purchase Request ${result.data?.prId || ''} raised successfully`)
        setShowSuccessModal(true)
      } else {
        setPrError(result.message || 'Something went wrong. Please try again.')
      }
    } catch (err) {
      setPrError('Network error. Please try again.')
    } finally {
      setPrLoading(false)
    }
  }

  useEffect(() => {
    const fetchModelsForBatches = async () => {
      try {
        const url = `${API_URL}models/${modelid}/batches/stores/${storeid}`
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })
        const result = await res.json()
        setModels(result.data.docs)
      } catch (error) {
        console.error(error)
      }
    }

    fetchModelsForBatches()
  }, [token, isEdit])

  useEffect(() => {
    if (models.length > 0) {
      setListPrice(models[0].listPrice || 0)
    }
  }, [models])

  return (
    <Fragment>
      <PageHeader
        pageName={`BATCHES | ${modelname} | ${partcode}`}
        breadcrump1='Home'
        breadcrump2='Batches'
        link='/dashboard'
      />
      <div className='p-4'>
        <CardBody className='bg-white p-3'>
          <form onSubmit={handleAddBatches}>
            <Row>
              <Col md={4}>
                <label htmlFor='name' className='form-label'>
                  Batch Date <span className='text-danger'>*</span>
                </label>
                <input
                  name='meetingDate'
                  value={batchDate}
                  onChange={e => setBatchDate(e.target.value)}
                  min={
                    new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)
                      .toISOString()
                      .split('T')[0]
                  }
                  max={new Date().toISOString().split('T')[0]}
                  className='form-control'
                  type='date'
                  id='meetingDate'
                  onClick={e => e.target.showPicker && e.target.showPicker()}
                  required
                />
              </Col>
              <Col md={4}>
                <label htmlFor='name' className='form-label'>
                  Quantity <span className='text-danger'>*</span>
                </label>
                <input
                  className='form-control mb-2'
                  type='number'
                  value={quantity}
                  onChange={e => setQuantity(e.target.value)}
                  placeholder='Enter Quantity'
                  required
                />
              </Col>
              <Col md={4}>
                <label htmlFor='name' className='form-label'>
                  Minimum Quantity <span className='text-danger'>*</span>
                </label>
                <input
                  className='form-control mb-2'
                  type='number'
                  value={minQuantity}
                  onChange={e => setMinQuantity(e.target.value)}
                  placeholder='Enter Minimum Quantity'
                  required
                />
              </Col>

              <Col md={3}>
                <label htmlFor='name' className='form-label'>
                  Max Quantity <span className='text-danger'>*</span>
                </label>
                <input
                  className='form-control mb-2'
                  type='number'
                  value={maxQuantity}
                  onChange={e => setMaxQuantity(e.target.value)}
                  placeholder='Max Quantity'
                  required
                />
              </Col>

              <Col md={4}>
                <label htmlFor='name' className='form-label'>
                  Purchase Price <span className='text-danger'>*</span>
                </label>
                <input
                  className='form-control mb-2'
                  placeholder='Enter Purchase Price'
                  type='number'
                  value={purchasePrice}
                  onChange={e => setPurchasePrice(e.target.value)}
                  required
                />
              </Col>

              <Col md={3}>
                <label htmlFor='name' className='form-label'>
                  List Price <span className='text-danger'>*</span>
                </label>
                <input
                  className='form-control mb-2'
                  placeholder='Enter List Price'
                  type='number'
                  value={listPrice}
                  onChange={e => setListPrice(e.target.value)}
                  required
                />
              </Col>

              {/* ── Action buttons ── */}
              <Col md={2} className='pt-2'>
                <br />
                <div className='d-flex gap-2'>
                  <button type='submit' className='btn add-btn text-white'>
                    Add
                  </button>

                  {/* Raise PR button — visible only to permitted roles */}
                  {STORE_PR_ROLES.includes(userDetails?.role) && (
                    <button
                      type='button'
                      className='btn btn-primary text-white'
                      title='Raise Purchase Request for this item in this store'
                      onClick={handleOpenRaisePR}
                    >
                      Raise PR
                    </button>
                  )}
                </div>
              </Col>
            </Row>
          </form>
        </CardBody>
        <CardBody>
          <div className='sm:overflow-auto sm:max-h-[400px] w-full  mb-3 '>
            <Table hover className='align-middle table-nowrap'>
              <thead className='table-light'>
                <tr>
                  <th>
                    <div className='flex items-center'>Batch Date</div>
                  </th>
                  <th>
                    <div className='flex items-center'>UOM</div>
                  </th>
                  <th>
                    <div className='flex items-center'>Inspected Qty</div>
                  </th>
                  <th>
                    <div className='flex items-center'>Stock Qty</div>
                  </th>
                  <th>
                    <div className='flex items-center'>Wrong Qty</div>
                  </th>
                  <th>
                    <div className='flex items-center'>Damaged Qty</div>
                  </th>
                  <th>
                    <div className='flex items-center'>Short Supply</div>
                  </th>
                  <th>
                    <div className='flex items-center'>Min Qty</div>
                  </th>
                  <th>
                    <div className='flex items-center'>Max Qty</div>
                  </th>
                  {userDetails?.role === 'admin' ||
                    userDetails?.role === 'super_admin' ? (
                    <>
                      <th>
                        <div className='flex items-center'>Purchase Price Per Unit</div>

                      </th>
                      <th>
                        <div className='flex items-center'>Total Purchase Price</div>
                      </th>
                    </>
                  ) : (
                    ''
                  )}
                  <th>
                    <div className='flex items-center'>List Price Per Unit</div>
                  </th>
                  <th>
                    <div className='flex items-center'>Edit</div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {models.map((data, index) => {
                  return (
                    <tr key={index}>
                      {isEdit === index ? (
                        <>
                          <td>
                            <input
                              className='form-control'
                              type='date'
                              value={editedBatchDate}
                              onChange={e => setEditedBatchDate(e.target.value)}
                            />
                          </td>
                          <td>{data?.model?.uom}</td>
                          <td>{data?.inspectedQuantity}</td>
                          <td>
                            <input
                              className='form-control'
                              type='number'
                              value={editedQuatinty}
                              onChange={e => setEditedQuantity(e.target.value)}
                            />
                          </td>
                          <td>{data?.wrongQuantity}</td>
                          <td>{data?.damagedQuantity}</td>
                          <td>{data?.shortSupply}</td>
                          <td>
                            <input
                              className='form-control'
                              type='number'
                              value={editedMinQuantity}
                              onChange={e => setEditedMinQuantity(e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              className='form-control'
                              type='number'
                              value={editedMaxQuantity}
                              onChange={e => setEditedMaxQuantity(e.target.value)}
                            />
                          </td>
                          {userDetails?.role === 'admin' ||
                            userDetails?.role === 'super_admin' ? (
                            <>
                              <td>
                                <input
                                  className='form-control'
                                  type='number'
                                  value={editedPurchasePrice}
                                  onChange={e =>
                                    setEditedPurchasePrice(e.target.value)
                                  }
                                />
                              </td>
                              <td>
                                {Number((editedPurchasePrice || 0) * (editedQuatinty || 0)).toLocaleString(
                                  'en-US',
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  }
                                )}
                              </td>
                            </>
                          ) : (
                            ''
                          )}
                          <td>
                            <span className='d-flex'>
                              <input
                                className='form-control'
                                type='number'
                                value={editedListPrice}
                                onChange={e =>
                                  setEditedListPrice(e.target.value)
                                }
                              />
                              <button
                                type='submit'
                                className='ml-1 btn add-btn text-white'
                                onClick={e => handleEditBatches(e, data?.id)}
                              >
                                Edit
                              </button>
                            </span>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>
                            {new Date(data?.batchDate).toLocaleDateString(
                              'en-GB',
                              {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              }
                            )}
                          </td>
                          <td>{data?.model?.uom}</td>
                          <td>{data?.inspectedQuantity}</td>
                          <td>{data?.quantity}</td>
                          <td>{data?.wrongQuantity}</td>
                          <td>{data?.damagedQuantity}</td>
                          <td>{data?.shortSupply}</td>
                          <td>{data?.minQuantity}</td>
                          <td>{data?.maxQuantity}</td>
                          {userDetails?.role === 'admin' ||
                            userDetails?.role === 'super_admin' ? (
                            <>
                              <td>
                                {Number(data?.purchasePrice || 0).toLocaleString(
                                  'en-US',
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  }
                                )}
                              </td>
                              <td>
                                {Number(data?.purchasePrice * data?.quantity || 0).toLocaleString(
                                  'en-US',
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  }
                                )}
                              </td>
                            </>
                          ) : (
                            ''
                          )}

                          <td>
                            {Number(data?.listPrice || 0).toLocaleString(
                              'en-US',
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }
                            )}
                          </td>
                        </>
                      )}
                      <td>
                        {index !== models.length - 1 ? (
                          <FontAwesomeIcon
                            onClick={() => {
                              setIsEdit(isEdit === null ? index : null)
                              setEditedBatchDate(
                                data?.batchDate.substring(0, 10)
                              )
                              setEditedPartCode(data?.partCode)
                              setEditedQuantity(data?.quantity)
                              setEditedMinQuantity(data?.minQuantity)
                              setEditedMaxQuantity(data?.maxQuantity)
                              setEditedPurchasePrice(data?.purchasePrice)
                              setEditedListPrice(data?.listPrice)
                            }}
                            className='text-warning'
                            icon={faPenToSquare}
                          />
                        ) : (
                          ''
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
        </CardBody>

        {/* ════════════════════════════════════════════════════════════════════
            Raise PR Modal
        ════════════════════════════════════════════════════════════════════ */}
        <Modal show={showRaisePRModal} centered onHide={() => setShowRaisePRModal(false)}>
          <Modal.Header closeButton className='border-0 pb-0'>
            <Modal.Title className='fw-semibold' style={{ fontSize: '1.1rem' }}>
              Raise Purchase Request
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className='pt-2'>
            <p className='text-muted small mb-3'>
              Raising PR for <strong>{modelname}</strong>{partcode ? ` (${partcode})` : ''} in this store.
            </p>
            <form onSubmit={handleRaisePR}>
              {/* Quantity */}
              <div className='mb-3'>
                <label className='form-label fw-medium'>
                  Quantity <span className='text-danger'>*</span>
                </label>
                <input
                  className='form-control'
                  type='number'
                  min='1'
                  value={prQuantity}
                  onChange={e => setPrQuantity(e.target.value)}
                  placeholder='Enter required quantity'
                  required
                />
                {models.length > 0 && models[models.length - 1]?.maxQuantity > 0 && (
                  <div className='form-text'>
                    Max stock level for this item: <strong>{models[models.length - 1]?.maxQuantity}</strong>
                  </div>
                )}
              </div>

              {/* Required Delivery Date */}
              <div className='mb-3'>
                <label className='form-label fw-medium'>
                  Required Delivery Date <span className='text-danger'>*</span>
                </label>
                <input
                  className='form-control'
                  type='date'
                  min={new Date().toISOString().split('T')[0]}
                  value={prDeliveryDate}
                  onChange={e => setPrDeliveryDate(e.target.value)}
                  onClick={e => e.target.showPicker && e.target.showPicker()}
                  required
                />
              </div>

              {/* OEM / Non-OEM */}
              <div className='mb-3'>
                <label className='form-label fw-medium d-block'>
                  PR Type <span className='text-danger'>*</span>
                </label>
                <div className='d-flex gap-4'>
                  <div className='form-check'>
                    <input
                      className='form-check-input'
                      type='radio'
                      name='prIsOem'
                      id='prNonOem'
                      checked={!prIsOem}
                      onChange={() => setPrIsOem(false)}
                    />
                    <label className='form-check-label' htmlFor='prNonOem'>
                      Non-OEM
                    </label>
                  </div>
                  <div className='form-check'>
                    <input
                      className='form-check-input'
                      type='radio'
                      name='prIsOem'
                      id='prOem'
                      checked={prIsOem}
                      onChange={() => setPrIsOem(true)}
                    />
                    <label className='form-check-label' htmlFor='prOem'>
                      OEM
                    </label>
                  </div>
                </div>
              </div>

              {/* Specification (optional) */}
              <div className='mb-3'>
                <label className='form-label fw-medium'>
                  Specification <span className='text-muted small'>(optional)</span>
                </label>
                <textarea
                  className='form-control'
                  rows={3}
                  value={prSpecification}
                  onChange={e => setPrSpecification(e.target.value)}
                  placeholder='Any remarks or specifications for this request…'
                />
              </div>

              {/* Inline error */}
              {prError && (
                <div className='alert alert-danger py-2 small mb-3'>
                  {prError}
                </div>
              )}

              {/* Footer actions */}
              <div className='d-flex justify-content-end gap-2'>
                <button
                  type='button'
                  className='btn btn-outline-secondary'
                  onClick={() => setShowRaisePRModal(false)}
                  disabled={prLoading}
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='btn btn-primary text-white'
                  disabled={prLoading}
                >
                  {prLoading ? 'Submitting…' : 'Raise PR'}
                </button>
              </div>
            </form>
          </Modal.Body>
        </Modal>

        {/* ════════════════════════════════════════════════════════════════════
            Success Modal
        ════════════════════════════════════════════════════════════════════ */}
        <Modal show={showSuccessModal} centered>
          <Modal.Body>
            <div class='my-8 text-center'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className={`w-14 shrink-0 ${modalMessage === 'Model Deleted Successfully'
                  ? 'fill-blue-500'
                  : 'fill-green-500'
                  }  inline`}
                viewBox='0 0 512 512'
              >
                <path
                  d='M383.841 171.838c-7.881-8.31-21.02-8.676-29.343-.775L221.987 296.732l-63.204-64.893c-8.005-8.213-21.13-8.393-29.35-.387-8.213 7.998-8.386 21.137-.388 29.35l77.492 79.561a20.687 20.687 0 0 0 14.869 6.275 20.744 20.744 0 0 0 14.288-5.694l147.373-139.762c8.316-7.888 8.668-21.027.774-29.344z'
                  data-original='#000000'
                />
                <path
                  d='M256 0C114.84 0 0 114.84 0 256s114.84 256 256 256 256-114.84 256-256S397.16 0 256 0zm0 470.487c-118.265 0-214.487-96.214-214.487-214.487 0-118.265 96.221-214.487 214.487-214.487 118.272 0 214.487 96.221 214.487 214.487 0 118.272-96.215 214.487-214.487 214.487z'
                  data-original='#000000'
                />
              </svg>
              <h4 className='text-xl text-gray-800 font-semibold mt-4'>
                Job Done
              </h4>
              <p className='text-sm text-gray-500 leading-relaxed mt-4'>
                {modalMessage}!
              </p>
            </div>

            <button
              type='button'
              onClick={handleCloseModal}
              className={`px-5 py-2.5 w-full rounded-lg text-white text-sm border-none outline-none ${modalMessage === 'Model Deleted Successfully'
                ? 'bg-blue-500 hover:bg-blue-700'
                : 'bg-green-500 hover:bg-green-700'
                } `}
            >
              Okay
            </button>
          </Modal.Body>
        </Modal>

        {/* ════════════════════════════════════════════════════════════════════
            Decline Modal
        ════════════════════════════════════════════════════════════════════ */}
        <Modal show={showDeclineModal} centered>
          <Modal.Body>
            <div class='my-8 text-center'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='w-14 fill-red-500 inline'
                viewBox='0 0 286.054 286.054'
              >
                <path
                  fill='#e2574c'
                  d='M143.027 0C64.04 0 0 64.04 0 143.027c0 78.996 64.04 143.027 143.027 143.027 78.996 0 143.027-64.022 143.027-143.027C286.054 64.04 222.022 0 143.027 0zm0 259.236c-64.183 0-116.209-52.026-116.209-116.209S78.844 26.818 143.027 26.818s116.209 52.026 116.209 116.209-52.026 116.209-116.209 116.209zm.009-196.51c-10.244 0-17.995 5.346-17.995 13.981v79.201c0 8.644 7.75 13.972 17.995 13.972 9.994 0 17.995-5.551 17.995-13.972V76.707c-.001-8.43-8.001-13.981-17.995-13.981zm0 124.997c-9.842 0-17.852 8.01-17.852 17.86 0 9.833 8.01 17.843 17.852 17.843s17.843-8.01 17.843-17.843c-.001-9.851-8.001-17.86-17.843-17.86z'
                  data-original='#e2574c'
                ></path>
              </svg>
              <h4 className='text-xl text-gray-800 font-semibold mt-4'>
                Error Occured
              </h4>
              <p className='text-sm text-gray-500 leading-relaxed mt-4'>
                Something went wrong!
              </p>
            </div>

            <button
              type='button'
              onClick={handleCloseModal}
              className='px-5 py-2.5 w-full rounded-lg text-white text-sm border-none outline-none bg-red-500 hover:bg-red-700'
            >
              Okay
            </button>
          </Modal.Body>
        </Modal>
      </div>
    </Fragment>
  )
}

export default BatchDetails
